// Gear lab: a menu screen for looking at how every class dresses as it collects equipment.
// Pick a class and a kit on the left; the hero walks in all four directions on the right, and
// the strip below shows the climb from bare to astral.

import { CLASSES, CLASS_IDS } from '../shared/classes';
import { ITEM_BASES, classItemBases } from '../shared/items';
import { GEAR_TIERS, GearLook, NO_GEAR, OffhandKind, TIER_NAMES, dressedLook } from '../shared/look';
import { ANIM_CAST, ANIM_IDLE, ANIM_SHOOT, ANIM_SLAM, ANIM_SWING, ANIM_WALK } from '../shared/protocol';
import { ClassId } from '../shared/types';
import { drawHeldWeapon, heldBehind } from './game/render';
import { blitHero, heldPose, heroFrame } from './gfx/sprites';
import { input } from './input';
import { C, ui } from './ui';

const RARITY_LABELS = ['Common', 'Magic', 'Rare', 'Legend'];
const POSES = ['Standing', 'Walking', 'Attacking'];

export class GearLab {
  cls: ClassId = 'reaver';
  gear: GearLook = { ...NO_GEAR };
  private time = 0;
  private pose = 1;
  private mode: 'kit' | 'everyone' = 'kit';
  private zoom = false;
  private zoomDir = 0;

  constructor() {
    this.setClass('reaver');
  }

  /** The given gear with a class's own weapon and off-hand. */
  private classGear(cls: ClassId, gear: GearLook): GearLook {
    const bases = classItemBases(cls);
    const weapon = bases.find((b) => b.kind === 'weapon');
    const off = bases.find((b) => b.kind === 'offhand');
    return {
      ...gear,
      weapon: weapon?.icon ?? '',
      weaponRarity: Math.max(0, gear.weaponRarity),
      weaponTier: Math.max(0, gear.weaponTier),
      offhand: (off?.icon as OffhandKind) ?? '',
    };
  }

  /** Switches class, giving it that class's own weapon and off-hand. */
  private setClass(cls: ClassId): void {
    this.cls = cls;
    this.gear = this.classGear(cls, this.gear);
  }

  /**
   * Every slot at one tier and rarity: the kit a hero of that level would be wearing. Bare means
   * empty-handed too, and a tiered kit after it takes the class's own weapon and off-hand back.
   */
  private kitAt(tier: number, rarity: number, base: GearLook = this.gear, cls: ClassId = this.cls): GearLook {
    const r = tier < 0 ? -1 : rarity;
    const kit = { ...base, weaponTier: tier, weaponRarity: r, armorTier: tier, armorRarity: r, helmTier: tier, helmRarity: r, offTier: tier, offRarity: r, glovesTier: tier, bootsTier: tier };
    if (tier < 0) {
      kit.weapon = '';
      kit.offhand = '';
    } else if (!kit.weapon || !kit.offhand) {
      const own = this.classGear(cls, kit);
      if (!kit.weapon) kit.weapon = own.weapon;
      if (!kit.offhand) kit.offhand = own.offhand;
    }
    return kit;
  }

  private drawDoll(cls: ClassId, gear: GearLook, x: number, y: number, dir: number, scale: number, animate: boolean): void {
    const g = ui.ctx;
    const walking = animate && this.pose === 1;
    const f = walking ? 1 + (Math.floor(this.time * 6) % 2) : 0;
    const tick = Math.floor(this.time * 3) % 2;
    const spr = heroFrame(dressedLook(cls, gear), dir, f, tick);
    // Compose the hero and weapon at the hero's density, then scale the lot so every pixel stays square.
    const w = 32;
    const h = 24;
    const tmp = labCanvas(w * 2, h * 2);
    const t = tmp.getContext('2d')!;
    t.setTransform(1, 0, 0, 1, 0, 0);
    t.clearRect(0, 0, w * 2, h * 2);
    t.setTransform(2, 0, 0, 2, 0, 0);
    t.imageSmoothingEnabled = false;
    const sx = 16;
    const sy = 20;
    // Attacking: guns fire, everything else swings, slams and casts, over and over.
    let anim = walking ? ANIM_WALK : ANIM_IDLE;
    let animT = 0;
    if (animate && this.pose === 2) {
      const cycle = this.time % 2;
      if (heldPose(gear.weapon) === 'flat') {
        if (cycle < 0.6) {
          anim = ANIM_SHOOT;
          animT = cycle;
        }
      } else if (cycle < 0.36) {
        anim = ANIM_SWING;
        animT = cycle;
      } else if (cycle >= 0.8 && cycle < 1.16) {
        anim = ANIM_SLAM;
        animT = cycle - 0.8;
      } else if (cycle >= 1.4) anim = ANIM_CAST;
    }
    const behind = heldBehind(gear.weapon, dir, anim, animT, this.time);
    if (behind) drawHeldWeapon(t, gear.weapon, gear.weaponTier, gear.weaponRarity, sx, sy, dir, anim, animT, this.time);
    blitHero(t, spr, sx - 8, sy - 17);
    if (!behind) drawHeldWeapon(t, gear.weapon, gear.weaponTier, gear.weaponRarity, sx, sy, dir, anim, animT, this.time);
    g.imageSmoothingEnabled = false;
    g.drawImage(tmp, x, y, w * scale, h * scale);
  }

  /** Returns true when the player wants to leave. */
  draw(W: number, H: number, dt: number): boolean {
    this.time += dt;
    let leave = false;
    ui.text('GEAR LAB', 6, 4, C.goldL, { scale: 2 });
    if (ui.button(W - 54, 4, 48, 13, 'Back') || input.pressed.has('Escape')) leave = true;
    if (ui.button(W - 120, 4, 62, 13, POSES[this.pose], { active: this.pose > 0 })) this.pose = (this.pose + 1) % POSES.length;
    if (ui.button(W - 178, 4, 54, 13, 'Everyone', { active: this.mode === 'everyone' })) this.mode = 'everyone';
    if (ui.button(W - 236, 4, 54, 13, 'Kit', { active: this.mode === 'kit' })) this.mode = 'kit';
    if (this.mode === 'everyone') this.drawEveryone(W, H);
    else this.drawKit(W, H);
    return leave;
  }

  private get rarity(): number {
    return Math.max(0, this.gear.armorRarity);
  }

  /** Pick a class and a kit; see it up close and across the tiers. */
  private drawKit(W: number, H: number): void {
    const cx = 6;
    let cy = 24;
    const bw = 24;
    const bh = 11;
    const g = this.gear;
    const tierRow = (label: string, value: number, set: (v: number) => void) => {
      ui.text(label, cx, cy + 2, C.text);
      for (let t = -1; t < GEAR_TIERS; t++) {
        const bx = cx + 44 + (t + 1) * (bw + 1);
        if (ui.button(bx, cy, bw, bh, t < 0 ? '-' : `T${t}`, { active: value === t })) set(t);
      }
      cy += bh + 2;
    };

    // Classes pick by portrait: the names would not fit on a row.
    ui.text('Class', cx, cy + 6, C.text);
    CLASS_IDS.forEach((id, i) => {
      const bx = cx + 44 + i * 21;
      if (ui.button(bx, cy, 20, 20, '', { active: this.cls === id })) this.setClass(id);
      blitHero(ui.ctx, heroFrame(CLASSES[id].look, 0, 0), bx + 2, cy + 1);
      if (ui.hit(bx, cy, 20, 20)) ui.tooltip(() => ui.textBox([{ s: CLASSES[id].name, c: C.goldL }]));
    });
    cy += 24;

    tierRow('Armor', g.armorTier, (v) => (g.armorTier = v));
    tierRow('Helm', g.helmTier, (v) => (g.helmTier = v));
    tierRow('Gloves', g.glovesTier, (v) => (g.glovesTier = v));
    tierRow('Boots', g.bootsTier, (v) => (g.bootsTier = v));

    ui.text('Offhand', cx, cy + 2, C.text);
    const offKinds: OffhandKind[] = ['', 'shield', 'orb', 'mag'];
    offKinds.forEach((k, i) => {
      const bx = cx + 44 + i * 41;
      if (ui.button(bx, cy, 40, bh, k || '-', { active: g.offhand === k })) g.offhand = k;
    });
    cy += bh + 2;
    tierRow('', g.offTier, (v) => (g.offTier = v));

    ui.text('Weapon', cx, cy + 2, C.text);
    const weapons = classItemBases(this.cls).filter((b) => b.kind === 'weapon');
    let wx = cx + 44;
    for (const b of weapons) {
      const label = ITEM_BASES[b.id].names[0].split(' ').pop() ?? b.id;
      const ww = Math.max(bw * 1.5, label.length * 6 + 6);
      if (ui.button(wx, cy, ww, bh, label, { active: g.weapon === b.icon })) g.weapon = b.icon;
      wx += ww + 1;
    }
    if (ui.button(wx, cy, bw, bh, '-', { active: !g.weapon })) g.weapon = '';
    cy += bh + 2;
    tierRow('', g.weaponTier, (v) => (g.weaponTier = v));

    // One rarity for everything worn: it only changes the trim color.
    ui.text('Rarity', cx, cy + 2, C.text);
    for (let r = 0; r < 4; r++) {
      const bx = cx + 44 + r * 41;
      if (ui.button(bx, cy, 40, bh, RARITY_LABELS[r], { active: this.rarity === r })) {
        g.armorRarity = r;
        g.helmRarity = r;
        g.offRarity = r;
        g.weaponRarity = r;
      }
    }
    cy += bh + 2;

    ui.text('Kit', cx, cy + 2, C.text);
    if (ui.button(cx + 44, cy, bw, bh, '-')) this.gear = this.kitAt(-1, this.rarity);
    for (let t = 0; t < GEAR_TIERS; t++) {
      if (ui.button(cx + 44 + (t + 1) * (bw + 1), cy, bw, bh, `T${t}`)) this.gear = this.kitAt(t, this.rarity);
    }
    cy += bh + 4;

    // Close-up: the four facings at 2x, or one facing at 4x to see the pixels.
    const scale = this.zoom ? 4 : 2;
    const pw = this.zoom ? 32 * scale + 12 : 32 * scale * 2 + 12;
    const px0 = W - pw - 6;
    const ph = this.zoom ? 24 * scale + 30 : 24 * scale * 2 + 16;
    ui.panel(px0, 22, pw, ph, `${CLASSES[this.cls].name}`);
    if (this.zoom) {
      this.drawDoll(this.cls, this.gear, px0 + 6, 34, this.zoomDir, scale, true);
      const names = ['Down', 'Up', 'Left', 'Right'];
      names.forEach((n, d) => {
        if (ui.button(px0 + 6 + d * 32, 34 + 24 * scale + 2, 31, 11, n, { active: this.zoomDir === d })) this.zoomDir = d;
      });
    } else {
      const dirs = [0, 3, 1, 2];
      dirs.forEach((d, i) => {
        this.drawDoll(this.cls, this.gear, px0 + 6 + (i % 2) * 32 * scale, 34 + Math.floor(i / 2) * 24 * scale, d, scale, true);
      });
    }
    if (ui.button(px0 + 6, 25, 24, 11, this.zoom ? '2x' : '4x')) this.zoom = !this.zoom;

    // The climb: every tier of a full kit at the chosen rarity.
    const sy = Math.max(cy, 22 + ph + 4);
    const stripH = H - sy - 4;
    ui.panel(6, sy, W - 12, stripH, 'The climb');
    const cols = GEAR_TIERS + 1;
    const colW = Math.floor((W - 24) / cols);
    const dollScale = colW >= 56 && stripH >= 66 ? 2 : 1;
    for (let t = -1; t < GEAR_TIERS; t++) {
      const kit = this.kitAt(t, this.rarity);
      const x = 12 + (t + 1) * colW;
      const label = t < 0 ? 'Bare' : `T${t} ${TIER_NAMES[t]}`;
      const hot = ui.hit(x, sy + 12, colW, stripH - 14);
      ui.text(label, x + (colW >> 1), sy + 12, hot || t === this.gear.armorTier ? C.goldL : C.dim, { align: 'center' });
      this.drawDoll(this.cls, kit, x + ((colW - 32 * dollScale) >> 1), sy + 19, 0, dollScale, true);
      if (hot && input.lmbPressed) this.gear = kit;
    }
  }

  /** Every class across every tier, at the chosen rarity. */
  private drawEveryone(W: number, H: number): void {
    const bw = 40;
    const bh = 11;
    ui.text('Rarity', 6, 24, C.text);
    for (let r = 0; r < 4; r++) {
      if (ui.button(46 + r * (bw + 1), 22, bw, bh, RARITY_LABELS[r], { active: this.rarity === r })) {
        this.gear.armorRarity = r;
        this.gear.helmRarity = r;
        this.gear.offRarity = r;
      }
    }
    const top = 38;
    const left = 70;
    const cols = GEAR_TIERS + 1;
    const colW = Math.floor((W - left - 8) / cols);
    const rowH = Math.floor((H - top - 14) / CLASS_IDS.length);
    const scale = colW >= 64 && rowH >= 48 ? 2 : 1;
    for (let t = -1; t < GEAR_TIERS; t++) {
      const x = left + (t + 1) * colW;
      ui.text(t < 0 ? 'Bare' : colW >= 66 ? `T${t} ${TIER_NAMES[t]}` : TIER_NAMES[t], x + (colW >> 1), top, C.dim, { align: 'center' });
    }
    // Each row dresses its own class in a copy of the chosen gear; the selection only changes on a click.
    let picked: ClassId | null = null;
    CLASS_IDS.forEach((id, r) => {
      const y = top + 12 + r * rowH;
      const hot = ui.hit(0, y, W, rowH);
      ui.text(CLASSES[id].name, 6, y + (rowH >> 1) - 3, hot || id === this.cls ? C.goldL : C.text);
      const base = this.classGear(id, this.gear);
      for (let t = -1; t < GEAR_TIERS; t++) {
        const x = left + (t + 1) * colW;
        this.drawDoll(id, this.kitAt(t, this.rarity, base, id), x + ((colW - 32 * scale) >> 1), y + ((rowH - 24 * scale) >> 1), 0, scale, true);
      }
      if (hot && input.lmbPressed) picked = id;
    });
    if (picked) {
      this.setClass(picked);
      this.mode = 'kit';
    }
  }
}

let scratch: HTMLCanvasElement | null = null;
function labCanvas(w: number, h: number): HTMLCanvasElement {
  if (!scratch) {
    scratch = document.createElement('canvas');
    scratch.width = w;
    scratch.height = h;
  }
  return scratch;
}
